import { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Stack,
  Tooltip
} from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { useDomainPresence } from 'src/Common/hooks/useDomainPresence';
import {
  useBootOrchestrator,
  useShutdownOrchestrator
} from '../../api/useOrchestratorBoot';
import {
  ControlPlaneDomain,
  deriveControlPlaneStatus
} from './controlPlaneStatus';

const LABEL: Record<ControlPlaneDomain, string> = {
  matching: 'matcher',
  opportunities: 'catálogo de pares'
};

const POD: Record<ControlPlaneDomain, string> = {
  matching: 'matching-orchestrator',
  opportunities: 'opportunities-orchestrator'
};

/**
 * While a transition is in flight the 30s default is too slow — this chip IS the
 * primary indicator for these two pods, and `useDomainPresence`'s own contract
 * says an indicator built on it needs the faster poll or the operator watches a
 * stale value for half a minute and presses the button again. Same 5s
 * `StopStartOpenerButtons` uses.
 */
const TRANSITION_POLL_MS = 5000;

/**
 * The explicit Boot / Shutdown pair for one domain's control pod, plus a chip
 * saying whether that pod is up.
 *
 * ONE component for every domain rather than a file per domain: the endpoints
 * and the error handling are identical, and `useOrchestratorBoot` already keys
 * everything off `domain`. The opener and unwinder keep their own richer headers
 * (engine Stop/Start, panic lockout, job stepper) — this is the whole control
 * surface for the domains that have nothing but a pod to raise and lower.
 */
export const ControlPlaneButtons = ({
  domain
}: {
  domain: ControlPlaneDomain;
}) => {
  const boot = useBootOrchestrator(domain);
  const shutdown = useShutdownOrchestrator(domain);
  // Two reads of the same shared cache entry: one to know the status, one to
  // decide the cadence. `inFlight` covers the mutations THIS component owns;
  // `starting`/`draining` cover a transition anyone started, including the pod
  // reconcile loop, which is the case a local flag can never see.
  const inFlight = boot.isLoading || shutdown.isLoading;
  const settled = useDomainPresence().get(domain);
  const transitioning =
    inFlight ||
    deriveControlPlaneStatus(settled) === 'starting' ||
    deriveControlPlaneStatus(settled) === 'draining';
  const { get: getDomainPresence } = useDomainPresence(
    transitioning ? { refetchIntervalMs: TRANSITION_POLL_MS } : undefined
  );

  // Shared with `BootDomainGate` on purpose — see `deriveControlPlaneStatus`.
  // Deriving this here independently is what let the chip render a green "UP"
  // for a draining pod while the gate right below said it was off.
  const status = deriveControlPlaneStatus(getDomainPresence(domain));

  const CHIP = {
    up: { suffix: 'UP', color: 'success' as const, filled: true },
    starting: { suffix: 'arrancando…', color: 'info' as const, filled: false },
    draining: { suffix: 'apagándose…', color: 'warning' as const, filled: false },
    notServing: {
      suffix: 'sin réplica lista',
      color: 'warning' as const,
      filled: false
    },
    down: { suffix: 'DOWN', color: 'default' as const, filled: false },
    unknown: { suffix: '?', color: 'warning' as const, filled: false }
  }[status];

  // Boot/shutdown answer 202 even on refusal (`{ ok: false, reason }`) —
  // `useOrchestratorBoot` turns that into a mutation error so this cannot be a
  // silent no-op: the button re-enabling is not proof the pod moved.
  const [error, setError] = useState<string | null>(null);

  const onError = (fallback: string) => (err: unknown) =>
    setError(err instanceof Error ? err.message : fallback);

  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip
          title={
            {
              up: `${POD[domain]} está arriba y respondiendo`,
              starting: `${POD[domain]} está escalado pero todavía sin réplica lista — sus requests siguen fallando`,
              draining: `${POD[domain]} está en 0 réplicas y su pod todavía se está muriendo — sus requests ya fallan`,
              notServing: `${POD[domain]} no tiene ninguna réplica lista — sus requests fallan. No se pudo leer su escala.`,
              down: `${POD[domain]} está en 0 réplicas`,
              unknown: `No se pudo leer el Deployment de ${POD[domain]} — estado desconocido`
            }[status]
          }
          placement="top"
        >
          <Chip
            size="small"
            data-testid={`control-plane-chip-${domain}`}
            label={`${LABEL[domain]}: ${CHIP.suffix}`}
            color={CHIP.color}
            variant={CHIP.filled ? 'filled' : 'outlined'}
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Tooltip>
        <Tooltip
          title={
            `Bootea el plano de control del ${LABEL[domain]} (pod ` +
            `${POD[domain]}). Sus rutas no responden hasta que el pod esté ` +
            `arriba. Idempotente.`
          }
          placement="top"
        >
          <span>
            <Button
              variant="text"
              color="info"
              size="small"
              startIcon={
                boot.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <CloudSyncIcon />
                )
              }
              onClick={() =>
                boot.mutate(undefined, {
                  onError: onError('Boot del control plane falló')
                })
              }
              disabled={boot.isLoading}
              sx={{ fontSize: '0.7rem' }}
            >
              Boot control plane
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={
            `Baja el plano de control del ${LABEL[domain]} (pod ` +
            `${POD[domain]}) a 0 réplicas. El backend REHÚSA si el dominio no ` +
            `está probadamente quieto.`
          }
          placement="top"
        >
          <span>
            <Button
              variant="text"
              color="inherit"
              size="small"
              startIcon={
                shutdown.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <CloudOffIcon />
                )
              }
              onClick={() =>
                shutdown.mutate(undefined, {
                  onError: onError('Shutdown del control plane falló')
                })
              }
              disabled={shutdown.isLoading}
              sx={{ fontSize: '0.7rem' }}
            >
              Shutdown control plane
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={8000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ fontSize: '0.8rem', maxWidth: 360 }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};
